import { ICloseEvent, IMessageEvent, w3cwebsocket as W3CWebSocket } from 'websocket';

import { API_CONFIG } from '../api/config/env';

/**
 * WS publisher(릴레이)가 fan-out 하는 콘솔 실시간 이벤트.
 *
 * 발신 서버(patient-api / survey-api)가 Kafka `feed.events` 로 발행한 신호를 릴레이가 그대로 전달한다.
 * PHI 는 없다 — `type`/`patientId` 로 "무엇이 바뀌었는지"만 알리고, 실제 데이터는 REST 로 재조회한다.
 *
 * 기존 PatientSocket(patient-api `/ws`) + SurveySocket(survey-api `/ws`) 2개를 릴레이 1개로 통합하는 클라이언트.
 */
export type FeedEventType =
  | 'USER_AGREED'
  | 'AGREEMENT_SENT'
  | 'PATIENT_CREATED'
  | 'PATIENT_UPDATED'
  | 'PATIENT_DELETED'
  | 'SURVEY_SESSION_CREATED'
  | 'SURVEY_SESSION_UPDATED'
  | 'SURVEY_SESSION_DELETED'
  | 'APPOINTMENT_CREATED'
  | 'APPOINTMENT_UPDATED'
  | 'APPOINTMENT_DELETED';

export interface FeedEvent {
  orgId: number;
  service: string; // 'patient' | 'survey'
  type: FeedEventType;
  patientId: string;
  occurredAt?: string;
}

export type FeedSubscribeId = number;

export interface FeedSocketClient {
  connected: boolean;
  /** 지정한 type(들)의 이벤트를 구독한다. 반환 id 로 unsubscribe. 연결 전에 호출해도 등록된다(재연결에도 유지). */
  subscribe: (types: FeedEventType | FeedEventType[], callback: (event: FeedEvent) => void) => FeedSubscribeId;
  unsubscribe: (id: FeedSubscribeId) => void;
  /** 연결(및 재연결)될 때마다 호출. 끊긴 동안 놓친 이벤트 보정을 위해 전체 refetch 트리거로 쓴다. */
  onConnect: (callback: () => void) => void;
  onClose: (callback: () => void) => void;
  deactivate: () => void;
}

interface Listener {
  id: FeedSubscribeId;
  types: FeedEventType[];
  callback: (event: FeedEvent) => void;
}

const NORMAL_CLOSURE_CODE = 1000;
const INVALID_TOKEN_CODE = 1011;
const HEARTBEAT_INTERVAL_MS = 60_000;
const RECONNECT_DELAY_MS = 5_000;

class FeedSocketImpl implements FeedSocketClient {
  private _client: W3CWebSocket;
  private hbIntervalId: ReturnType<typeof setInterval>;
  private listeners: Listener[] = [];
  private currId = 0;
  private onConnectCb?: () => void;
  private onCloseCb?: () => void;
  private reconnectTimeoutId?: ReturnType<typeof setTimeout>;
  private deactivated = false;
  connected = false;

  constructor(client: W3CWebSocket) {
    this._client = client;
    this.hbIntervalId = this.startHeartBeat();
    this.init();
  }

  private startHeartBeat() {
    return setInterval(() => {
      if (this._client.readyState === W3CWebSocket.OPEN) {
        this._client.send('');
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private init() {
    this._client.onopen = () => {
      this.connected = true;
      this.onConnectCb?.();
    };
    this._client.onclose = (e: ICloseEvent) => {
      this.connected = false;
      this.onCloseCb?.();
      // 정상 종료/토큰 무효/deactivate 가 아니면 재연결. listeners 는 인스턴스 필드라 재연결 후에도 유지된다.
      if (!this.deactivated && e.code !== NORMAL_CLOSURE_CODE && e.code !== INVALID_TOKEN_CODE) {
        this.reconnectTimeoutId = setTimeout(() => {
          if (this.deactivated) return;
          this._client = new W3CWebSocket(this._client.url);
          this.init();
        }, RECONNECT_DELAY_MS);
      }
    };
    this._client.onerror = (error: Error) => {
      console.log(error);
    };
    this._client.onmessage = (message: IMessageEvent) => {
      try {
        const event: FeedEvent = JSON.parse(message.data as string);
        this.listeners.forEach((listener) => {
          if (listener.types.includes(event.type)) {
            listener.callback(event);
          }
        });
      } catch {
        // heartbeat/ping 등 JSON 이 아닌 메시지는 무시
      }
    };
  }

  subscribe(
    types: FeedEventType | FeedEventType[],
    callback: (event: FeedEvent) => void
  ): FeedSubscribeId {
    const id = this.currId++;
    this.listeners.push({ id, types: Array.isArray(types) ? types : [types], callback });
    return id;
  }

  unsubscribe(id: FeedSubscribeId) {
    this.listeners = this.listeners.filter((listener) => listener.id !== id);
  }

  onConnect(callback: () => void) {
    this.onConnectCb = callback;
    if (this.connected) {
      callback();
    }
  }

  onClose(callback: () => void) {
    this.onCloseCb = callback;
  }

  deactivate() {
    this.deactivated = true;
    clearInterval(this.hbIntervalId);
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
    }
    this.listeners = [];
    this.onConnectCb = undefined;
    this.onCloseCb = undefined;
    // 재연결 대기/연결 중(CONNECTING)이어도 확실히 종료 — 소유자 없는 좀비 소켓/로그아웃 후 재조회 방지.
    if (
      this._client.readyState === W3CWebSocket.OPEN ||
      this._client.readyState === W3CWebSocket.CONNECTING
    ) {
      this._client.close(NORMAL_CLOSURE_CODE);
    }
  }
}

export const FeedSocket = {
  /**
   * 릴레이 `/ws` 에 연결한다. 핸드셰이크 토큰은 쿼리 파라미터로 전달한다(브라우저 WS 가 헤더 불가).
   * @param token    business user JWT
   * @param services 구독할 발신 서비스명. 미지정 시 해당 org 의 전체 서비스 수신.
   */
  connect: (token: string, services: string[] = ['patient', 'survey']): FeedSocketClient => {
    const base = (API_CONFIG.FEED_URL || 'http://localhost:8080')
      .replace(/^https/, 'wss')
      .replace(/^http:/, 'ws:');
    // 반복 파라미터로 전송(?subscribeServices=a&subscribeServices=b) → 서버가 List 로 자동 파싱(표준).
    const subscribe = services.map((s) => `&subscribeServices=${encodeURIComponent(s)}`).join('');
    return new FeedSocketImpl(new W3CWebSocket(`${base}/ws?Authorization=Bearer ${token}${subscribe}`));
  },
};
